import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useUpgradeDialog from '../../../hooks/useUpgradeDialog';
import { requestSchoolPlan, submitSchoolPlanEnquiry } from '../../../service';
import {
  DEMO_URL,
  EMPTY_VALUES,
  LIMITS,
  OTHER_SUBJECT,
  ROLE_OPTIONS,
  SALES_PHONE_DISPLAY,
  SALES_PHONE_HREF,
  STUDENT_COUNT_OPTIONS,
  SUBJECT_OPTIONS,
  applyPrefill,
  contactNeedsEditing,
  enquiryPayload,
  initials,
  toggleSubject,
  validate,
} from './enquiryForm';
import * as S from './style';
import { track as trackProductEvent } from '../../../utils/productEvents';

const BENEFITS = [
  'Reduce marking load for teachers',
  'Give students faster, curriculum-aligned feedback',
  'Identify skill gaps across every class',
];
const INCLUDES = [
  'Classes & tasks',
  'Class insights',
  'Student AI controls',
  'LMS integrations',
  'Peer & self-assessment',
  'Onboarding & training',
];
const NEXT_STEPS = [
  'A quote sized to your school, by email',
  'A short call to answer questions and plan your rollout',
  'Onboarding and training for your teachers',
];
const FIELD_ORDER = [
  'firstName',
  'email',
  'schoolName',
  'role',
  'phone',
  'subjects',
  'otherSubjects',
  'studentCount',
];
const SEND_FAILED =
  'We couldn’t send your details. Check your connection and try again, or call us on ' +
  SALES_PHONE_DISPLAY +
  '.';

const Check = ({ size = 14, weight = 2.8 }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={weight}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m5 12 4 4L19 6" />
  </svg>
);
const Calendar = ({ size = 16 }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M7 2v6m10-6v6M3 11h18" />
  </svg>
);
const Phone = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
  </svg>
);
const Chevron = () => (
  <svg
    aria-hidden="true"
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    stroke="#1D1541"
    strokeWidth="2"
  >
    <path d="m1 1 5 5 5-5" />
  </svg>
);

// Product-event source for the free-text `source` prop.
const PLAN_SOURCES = {
  'Upgrade popup': 'upgrade_popup',
  'Locked feature': 'locked_feature',
  'Mobile account': 'mobile_account',
};
const planSource = (source) => PLAN_SOURCES[source] || 'other';

// Best-effort RUM action so the enquiry funnel can be measured; absent in tests.
const track = (name, attributes) => {
  try {
    // eslint-disable-next-line global-require
    const { datadogRum } = require('@datadog/browser-rum');
    datadogRum?.addAction?.(name, attributes);
  } catch (e) {
    // RUM not present — non-fatal.
  }
};

/**
 * The school plan enquiry form behind every "Request a quote" (desktop upgrade popup, locked-feature
 * modal, mobile account). Opening it emails the sales inbox straight away, so a teacher who closes
 * it is still followed up; submitting sends their answers. Portalled to <body> so no transformed
 * ancestor pins it, and layered above the mobile shell.
 */
const SchoolPlanEnquiry = ({ isOpen, onClose, source = '', trigger = '' }) => {
  // useId's colons are valid ids but awkward in selectors; strip them.
  const titleId = useId().replace(/:/g, '');
  const [values, setValues] = useState(EMPTY_VALUES);
  const [editingContact, setEditingContact] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [notified, setNotified] = useState(false);
  const [tried, setTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sent, setSent] = useState(false);
  const openCountRef = useRef(0);
  const requestedRef = useRef(false);
  const submittingRef = useRef(false);
  const closeIfIdle = () => {
    if (!submittingRef.current) onClose?.();
  };
  const dialogRef = useUpgradeDialog(isOpen, closeIfIdle);

  // Each opening is a fresh request: reset the form, then email the sales inbox once and prefill
  // whatever fields the teacher has not started typing into. A reply for an earlier opening is
  // ignored, and the request is not repeated if the effect re-runs (StrictMode) within one opening.
  useEffect(() => {
    if (!isOpen) {
      requestedRef.current = false;
      return;
    }
    if (requestedRef.current) return;
    requestedRef.current = true;
    openCountRef.current += 1;
    const opening = openCountRef.current;
    setValues(EMPTY_VALUES);
    setEditingContact(false);
    setTried(false);
    setSubmitError('');
    setSent(false);
    setNotified(false);
    setPrefillLoading(true);
    track('school_plan_enquiry_opened', { source, trigger });
    trackProductEvent('school_plan_request_opened', { source: planSource(source) });
    Promise.resolve(requestSchoolPlan({ source, trigger }))
      .catch(() => null)
      .then((receipt) => {
        if (openCountRef.current !== opening) return;
        setPrefillLoading(false);
        setNotified(Boolean(receipt?.notified));
        // The contact fields sit behind "Loading your details…" until now, so only the prefill decides
        // whether they need typing in (no name or email on file, or the request failed).
        setEditingContact(
          contactNeedsEditing(applyPrefill(EMPTY_VALUES, receipt?.prefill))
        );
        setValues((current) => applyPrefill(current, receipt?.prefill));
      });
  }, [isOpen]);

  if (!isOpen) return null;

  const errors = tried ? validate(values) : {};
  const setField = (key) => (event) => {
    const value = event.target.value;
    setValues((current) => ({ ...current, [key]: value }));
  };
  const pickSubject = (subject) =>
    setValues((current) => ({
      ...current,
      subjects: toggleSubject(current.subjects, subject),
    }));
  const pickStudentCount = (studentCount) =>
    setValues((current) => ({ ...current, studentCount }));

  const focusFirstError = (found) => {
    const first = FIELD_ORDER.find((key) => found[key]);
    if (first === 'firstName' || first === 'email') setEditingContact(true);
    requestAnimationFrame(() =>
      dialogRef.current?.querySelector(`[data-field="${first}"]`)?.focus()
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    setTried(true);
    const found = validate(values);
    if (Object.keys(found).length) {
      focusFirstError(found);
      return;
    }
    const opening = openCountRef.current;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    const result = await submitSchoolPlanEnquiry(
      enquiryPayload(values, { source, trigger })
    );
    submittingRef.current = false;
    if (openCountRef.current !== opening || !requestedRef.current) return;
    setSubmitting(false);
    if (result !== true) {
      // undefined = the session expired and service.js is already sending them to sign in.
      if (result !== undefined) setSubmitError(SEND_FAILED);
      return;
    }
    track('school_plan_enquiry_sent', { source, trigger });
    trackProductEvent('school_plan_request_submitted', { source: planSource(source) });
    setSent(true);
  };

  const fieldError = (key) => errors[key] || null;
  const fullName = `${values.firstName} ${values.lastName}`.trim();

  return createPortal(
    <S.Overlay>
      <S.Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <S.CloseButton
          type="button"
          aria-label="Close"
          onClick={closeIfIdle}
          disabled={submitting}
        >
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </S.CloseButton>

        <S.Benefits>
          <div>
            <S.Eyebrow>School plan</S.Eyebrow>
            <S.BenefitsTitle>
              Less marking for teachers. Faster feedback for students.
            </S.BenefitsTitle>
          </div>
          <S.BenefitList>
            {BENEFITS.map((benefit) => (
              <li key={benefit}>
                <S.CheckBadge>
                  <Check size={15} weight={2.6} />
                </S.CheckBadge>
                <span>{benefit}</span>
              </li>
            ))}
          </S.BenefitList>
          <div>
            <S.IncludesLabel>Everything in Pro, plus</S.IncludesLabel>
            <S.Tags>
              {INCLUDES.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </S.Tags>
          </div>
          <S.TalkBlock>
            Prefer to talk?
            <a href={SALES_PHONE_HREF}>
              <Phone />
              <span>Call {SALES_PHONE_DISPLAY}</span>
            </a>
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
              <Calendar />
              <span>Book a 15-min demo</span>
            </a>
          </S.TalkBlock>
        </S.Benefits>

        <S.Main>
          {sent ? (
            <S.Sent>
              <S.SuccessIcon>
                <Check size={28} weight={2.6} />
              </S.SuccessIcon>
              <S.Header>
                <S.Title id={titleId} style={{ marginTop: 0 }}>
                  Thanks
                  {values.firstName.trim()
                    ? `, ${values.firstName.trim()}`
                    : ''}
                  . Your quote is on its way.
                </S.Title>
                <S.Lead>Our team will send you pricing within 24 hours.</S.Lead>
              </S.Header>
              <S.NextSteps>
                <S.NextStepsLabel>What happens next</S.NextStepsLabel>
                <ol>
                  {NEXT_STEPS.map((step, index) => (
                    <li key={step}>
                      <S.CheckBadge $dark aria-hidden="true">
                        {index + 1}
                      </S.CheckBadge>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </S.NextSteps>
              <S.Actions>
                <S.PrimaryButton type="button" $grow onClick={closeIfIdle}>
                  Back to JeddAI
                </S.PrimaryButton>
                <S.SecondaryLink
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Calendar size={17} />
                  Pick a call time now
                </S.SecondaryLink>
              </S.Actions>
            </S.Sent>
          ) : (
            <S.Form
              onSubmit={handleSubmit}
              noValidate
              aria-busy={submitting || undefined}
            >
              <S.Header>
                {notified ? (
                  <S.SentPill>
                    <Check />
                    Request sent to our school team
                  </S.SentPill>
                ) : null}
                <S.Title id={titleId}>Get your school quote</S.Title>
                <S.Lead>
                  Tell us a little about your school and we’ll tailor pricing to
                  fit. It takes about 30 seconds.
                </S.Lead>
              </S.Header>

              {editingContact ? (
                <S.Row>
                  <S.Field>
                    <S.Label htmlFor={`${titleId}-first`}>
                      First name <S.Required aria-hidden="true">*</S.Required>
                    </S.Label>
                    <S.Input
                      id={`${titleId}-first`}
                      data-field="firstName"
                      value={values.firstName}
                      onChange={setField('firstName')}
                      maxLength={LIMITS.name}
                      autoComplete="given-name"
                      aria-required="true"
                      aria-invalid={fieldError('firstName') ? true : undefined}
                    />
                    {fieldError('firstName') ? (
                      <S.ErrorText>{fieldError('firstName')}</S.ErrorText>
                    ) : null}
                  </S.Field>
                  <S.Field>
                    <S.Label htmlFor={`${titleId}-last`}>Last name</S.Label>
                    <S.Input
                      id={`${titleId}-last`}
                      value={values.lastName}
                      onChange={setField('lastName')}
                      maxLength={LIMITS.name}
                      autoComplete="family-name"
                    />
                  </S.Field>
                  <S.Field $wide>
                    <S.Label htmlFor={`${titleId}-email`}>
                      School email <S.Required aria-hidden="true">*</S.Required>
                    </S.Label>
                    <S.Input
                      id={`${titleId}-email`}
                      data-field="email"
                      type="email"
                      value={values.email}
                      onChange={setField('email')}
                      maxLength={LIMITS.email}
                      autoComplete="email"
                      placeholder="name@school.edu.au"
                      aria-required="true"
                      aria-invalid={fieldError('email') ? true : undefined}
                    />
                    {fieldError('email') ? (
                      <S.ErrorText>{fieldError('email')}</S.ErrorText>
                    ) : null}
                  </S.Field>
                </S.Row>
              ) : (
                <S.ContactCard aria-busy={prefillLoading || undefined}>
                  {prefillLoading ? (
                    <S.ContactText>
                      <span>Loading your details…</span>
                    </S.ContactText>
                  ) : (
                    <>
                      <S.Avatar aria-hidden="true">
                        {initials(values.firstName, values.lastName)}
                      </S.Avatar>
                      <S.ContactText>
                        <strong>{fullName}</strong>
                        <span>{values.email}</span>
                      </S.ContactText>
                      <S.TextButton
                        type="button"
                        aria-label="Edit your contact details"
                        onClick={() => setEditingContact(true)}
                      >
                        Edit
                      </S.TextButton>
                    </>
                  )}
                </S.ContactCard>
              )}

              <S.Field>
                <S.Label htmlFor={`${titleId}-school`}>
                  School / organisation{' '}
                  <S.Required aria-hidden="true">*</S.Required>
                </S.Label>
                <S.Input
                  id={`${titleId}-school`}
                  data-field="schoolName"
                  value={values.schoolName}
                  onChange={setField('schoolName')}
                  maxLength={LIMITS.schoolName}
                  placeholder="School or organisation name"
                  autoComplete="organization"
                  aria-required="true"
                  aria-invalid={fieldError('schoolName') ? true : undefined}
                />
                {fieldError('schoolName') ? (
                  <S.ErrorText>{fieldError('schoolName')}</S.ErrorText>
                ) : null}
              </S.Field>

              <S.Row>
                <S.Field>
                  <S.Label htmlFor={`${titleId}-role`}>
                    Your role <S.Required aria-hidden="true">*</S.Required>
                  </S.Label>
                  <S.SelectWrap>
                    <S.Select
                      id={`${titleId}-role`}
                      data-field="role"
                      value={values.role}
                      onChange={setField('role')}
                      aria-required="true"
                      aria-invalid={fieldError('role') ? true : undefined}
                    >
                      <option value="">Select your role</option>
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </S.Select>
                    <Chevron />
                  </S.SelectWrap>
                  {fieldError('role') ? (
                    <S.ErrorText>{fieldError('role')}</S.ErrorText>
                  ) : null}
                </S.Field>
                <S.Field>
                  <S.Label htmlFor={`${titleId}-phone`}>
                    Contact number <S.Required aria-hidden="true">*</S.Required>
                  </S.Label>
                  <S.Input
                    id={`${titleId}-phone`}
                    data-field="phone"
                    type="tel"
                    value={values.phone}
                    onChange={setField('phone')}
                    maxLength={LIMITS.phone}
                    placeholder="0412 345 678"
                    autoComplete="tel"
                    aria-required="true"
                    aria-invalid={fieldError('phone') ? true : undefined}
                  />
                  {fieldError('phone') ? (
                    <S.ErrorText>{fieldError('phone')}</S.ErrorText>
                  ) : null}
                </S.Field>
              </S.Row>

              <S.Fieldset>
                <S.Legend>
                  Subjects of interest{' '}
                  <S.Required aria-hidden="true">*</S.Required>
                </S.Legend>
                <S.ChipGroup>
                  {SUBJECT_OPTIONS.map((subject, index) => {
                    const selected = values.subjects.includes(subject);
                    return (
                      <S.Chip
                        key={subject}
                        type="button"
                        $pill
                        data-field={index === 0 ? 'subjects' : undefined}
                        aria-pressed={selected}
                        $invalid={Boolean(fieldError('subjects'))}
                        onClick={() => pickSubject(subject)}
                      >
                        {selected ? <Check /> : null}
                        {subject}
                      </S.Chip>
                    );
                  })}
                </S.ChipGroup>
                {fieldError('subjects') ? (
                  <S.ErrorText $spaced>{fieldError('subjects')}</S.ErrorText>
                ) : null}
              </S.Fieldset>

              {values.subjects.includes(OTHER_SUBJECT) ? (
                <S.Field>
                  <S.Label htmlFor={`${titleId}-other`}>
                    Which other subjects?{' '}
                    <S.Required aria-hidden="true">*</S.Required>
                  </S.Label>
                  <S.Input
                    id={`${titleId}-other`}
                    data-field="otherSubjects"
                    value={values.otherSubjects}
                    onChange={setField('otherSubjects')}
                    maxLength={LIMITS.otherSubjects}
                    placeholder="e.g. Legal Studies, Music"
                    aria-required="true"
                    aria-invalid={
                      fieldError('otherSubjects') ? true : undefined
                    }
                  />
                  {fieldError('otherSubjects') ? (
                    <S.ErrorText>{fieldError('otherSubjects')}</S.ErrorText>
                  ) : null}
                </S.Field>
              ) : null}

              <S.Fieldset>
                <S.Legend>
                  How many students would use Jeddle?{' '}
                  <S.Required aria-hidden="true">*</S.Required>
                </S.Legend>
                <S.ChipGroup $grid>
                  {STUDENT_COUNT_OPTIONS.map((count, index) => (
                    <S.Chip
                      key={count}
                      type="button"
                      data-field={index === 0 ? 'studentCount' : undefined}
                      aria-pressed={values.studentCount === count}
                      $invalid={Boolean(fieldError('studentCount'))}
                      onClick={() => pickStudentCount(count)}
                    >
                      {count}
                    </S.Chip>
                  ))}
                </S.ChipGroup>
                {fieldError('studentCount') ? (
                  <S.ErrorText $spaced>
                    {fieldError('studentCount')}
                  </S.ErrorText>
                ) : null}
              </S.Fieldset>

              <S.Field>
                <S.Label htmlFor={`${titleId}-note`}>
                  Anything we should know? <S.Optional>(optional)</S.Optional>
                </S.Label>
                <S.TextArea
                  id={`${titleId}-note`}
                  rows={2}
                  value={values.note}
                  onChange={setField('note')}
                  maxLength={LIMITS.note}
                  placeholder="e.g. Interested in access for my 7-12 English classes"
                />
              </S.Field>

              {submitError ? (
                <S.FormError role="alert">{submitError}</S.FormError>
              ) : null}

              <S.SubmitArea>
                <S.PrimaryButton type="submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Get my school quote'}
                  {submitting ? null : (
                    <svg
                      aria-hidden="true"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  )}
                </S.PrimaryButton>
                <S.FinePrint data-phone-only>
                  No commitment. Prefer to talk?{' '}
                  <a href={SALES_PHONE_HREF}>{SALES_PHONE_DISPLAY}</a> or{' '}
                  <a href={DEMO_URL} target="_blank" rel="noopener noreferrer">
                    book a demo
                  </a>
                </S.FinePrint>
                <S.FinePrint>
                  By submitting, you agree to our{' '}
                  <a
                    href="https://jeddle.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </S.FinePrint>
              </S.SubmitArea>
            </S.Form>
          )}
        </S.Main>
      </S.Dialog>
    </S.Overlay>,
    document.body
  );
};

export default SchoolPlanEnquiry;
